import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Auth from './pages/Auth';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ScrollToTop from './components/ScrollToTop';
import { useAuthStore } from './store/authStore';
import OrderTracking from './pages/OrderTracking';
import Contact from './pages/Contact';
import Profile from './pages/Profile';
import { supabase } from './lib/supabase';
import { Toaster } from './components/ui/sonner';
import './styles/animations.css';
import AdminRoute from './routes/AdminRoute';
import AdminOnlyRoute from './routes/AdminOnlyRoute';
import AdminDashboard from './pages/AdminDashboard';
import AdminMfaSetup from './pages/AdminMfaSetup';
import AdminMfaChallenge from './pages/AdminMfaChallenge';
import OrderDetail from './components/OrderDetail';
import { useAdminHomeScreen } from './hooks/useAdminHomeScreen';
import { useCartSync } from './hooks/useCartSync';
import CheckoutSuccess from './pages/CheckoutSuccess';
import Categories from './pages/Categories';
import Terms from './pages/Terms';
import Returns from './pages/Returns';
import ShippingPolicy from './pages/ShippingPolicy';
import NotFound from './pages/NotFound';
import Honey from './pages/Honey';
import Recipes from './pages/Recipes';
import RecipeDetail from './pages/RecipeDetail';
import Posts from './pages/Posts';
import PostDetail from './pages/PostDetail';

function App() {
  const { checkUser } = useAuthStore();
  const isAdminArea = useLocation().pathname.startsWith('/admin');
  useAdminHomeScreen(isAdminArea);
  useCartSync();

  useEffect(() => {
    // Initialize auth state when the app loads
    const initAuth = async () => {
      await checkUser();
    };
    
    initAuth();
    
    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        checkUser();
      } else {
        checkUser();
      }
    });

    // Clean up subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [checkUser]);

  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground transition-all duration-300">
      <Toaster richColors closeButton />
      {/* Navbar */}
      <Navbar />

      {/* Soft transitions */}
      <main className="flex-grow animate-fadeInUp">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/il-miele" element={<Honey />} />
          <Route path="/ricette" element={<Recipes />} />
          <Route path="/ricette/:slug" element={<RecipeDetail />} />
          <Route path="/novita" element={<Posts />} />
          <Route path="/novita/:slug" element={<PostDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/condizioni-di-vendita" element={<Terms />} />
          <Route path="/resi" element={<Returns />} />
          <Route path="/spedizioni" element={<ShippingPolicy />} />
          <Route path="/orders" element={<OrderTracking />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/checkout-success" element={<CheckoutSuccess />} />
          <Route element={<AdminOnlyRoute />}>
            <Route path="/admin/mfa-setup" element={<AdminMfaSetup />} />
            <Route path="/admin/mfa-challenge" element={<AdminMfaChallenge />} />
          </Route>
          <Route element={<AdminRoute />}>
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/orders" element={<Navigate to="/admin/dashboard?view=ordini" replace />} />
            <Route path="/admin/orders/:orderId" element={<OrderDetail />} />
            {/* Altre route per admin */}
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      {/* Footer: non serve nell'area admin */}
      {!isAdminArea && <Footer />}
      
      {/* Scroll to Top Button */}
      <ScrollToTop />
    </div>
  );
}

export default App;
